/**
 * Main data hook for creating chats
 * Follows GraphQL separation pattern - orchestrates all chat creation operations
 */

import type {
  CreateChatFormData,
  CreatedChatResponse,
  CreateChatConfig,
} from '@choco-admin/types';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../../../contexts/NotificationContext';
import { useCreateChatMutation } from '../../../generated';
import { usePuppyMode } from '../../../hooks';
import {
  mapToCreatedChatResponse,
  mapToGraphQLChatCreateInput,
  extractUserIds,
  // extractSubCatalogIds, // Currently unused
} from '../utils/createChatMapper';

export interface UseCreateChatResponse {
  createChat: (formData: CreateChatFormData) => Promise<CreatedChatResponse>;
  isLoading: boolean;
  error: Error | null;
}

export const useCreateChat = (
  config: CreateChatConfig = {}
): UseCreateChatResponse => {
  const { onSuccess, onError, captureException } = config;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { puppyFilter } = usePuppyMode();

  // Generated GraphQL mutation hooks
  const [createChatMutation] = useCreateChatMutation();

  const createChat = useCallback(
    async (formData: CreateChatFormData): Promise<CreatedChatResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Create the chat
        const userIds = extractUserIds(formData);
        const chatCreateInput = {
          ...mapToGraphQLChatCreateInput({
            chatName: formData.chatName,
            restaurantName: formData.restaurantName,
            deliveryAddress: formData.deliveryAddress,
            customerNumber: formData.customerNumber,
            additionalEmails: formData.additionalEmails,
            type: formData.chatType?.value,
            supplierId: formData.supplier?.value,
            buyerId: formData.buyer?.value,
            userIds,
            createdContext: formData.createdContext?.value,
            customerLevelMovAmount: formData.customerLevelMovAmount,
            customerLevelMovCurrency: formData.customerLevelMovCurrency,
          }),
          isPuppy: puppyFilter || false,
        };

        const { data: chatData } = await createChatMutation({
          variables: {
            chat: chatCreateInput,
          },
          refetchQueries: [
            'ChatSearchData', // Refetch chat list
          ],
          awaitRefetchQueries: true,
        });

        if (!chatData?.chatCreate) {
          throw new Error('Failed to create chat - no data returned');
        }

        const createdChat = mapToCreatedChatResponse(chatData.chatCreate);
        const chatId = createdChat.id;

        // Additional chat configuration can be added here in future updates

        // Success notification
        addNotification({
          title: 'Chat Created',
          message: `Chat "${
            createdChat.chatName || createdChat.restaurantName || 'New Chat'
          }" was successfully created.`,
          type: 'success',
        });

        // Call success callback
        onSuccess?.(createdChat);

        // Navigate to the created chat
        navigate(`/chats/${chatId}`);

        return createdChat;
      } catch (err) {
        const chatError = err as Error;
        setError(chatError);

        // Error notification
        addNotification({
          title: 'Failed to Create Chat',
          message:
            chatError.message ||
            'An unexpected error occurred while creating the chat.',
          type: 'error',
        });

        // Call error callback and capture exception
        onError?.(chatError);
        captureException?.(chatError);

        throw chatError;
      } finally {
        setIsLoading(false);
      }
    },
    [
      createChatMutation,
      puppyFilter,
      addNotification,
      navigate,
      onSuccess,
      onError,
      captureException,
    ]
  );

  return {
    createChat,
    isLoading,
    error,
  };
};
